import React, { useState, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import { IoMdClose } from 'react-icons/io';
import { IoIosArrowDown } from 'react-icons/io';
import { FiRefreshCw, FiUsers } from 'react-icons/fi';

import { DataState } from '../../context/DataProvider';
import { API_URL } from '../../services/api';

const TeamsConfigurationModal = ({ onClose }) => {
  const { userGoogleId } = DataState();
  const [activeTab, setActiveTab] = useState('selection');
  const [loading, setLoading] = useState(true);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [toggleLoading, setToggleLoading] = useState(null);

  const [teams, setTeams] = useState([]);
  const [channels, setChannels] = useState([]);
  const [chats, setChats] = useState([]);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  const [selectedTeams, setSelectedTeams] = useState(new Set());
  const [selectedChannels, setSelectedChannels] = useState({});
  const [selectedChats, setSelectedChats] = useState(new Set());
  const [expandedTeamId, setExpandedTeamId] = useState(null);

  const processFetchedData = (data) => {
    setTeams(data.teams || []);
    setChannels(data.channels || []);
    setChats(data.chats || []);
    setLastRefreshed(data.last_refreshed);

    const initialSelectedTeams = new Set(
      data.teams?.filter((t) => t.isSelected).map((t) => t.team_id) || [],
    );
    const initialSelectedChats = new Set(
      data.chats?.filter((c) => c.isSelected).map((c) => c.chat_id) || [],
    );
    const initialSelectedChannels = {};
    data.channels?.forEach((c) => {
      if (c.isSelected) {
        if (!initialSelectedChannels[c.team_id])
          initialSelectedChannels[c.team_id] = new Set();
        initialSelectedChannels[c.team_id].add(c.channel_id);
      }
    });
    setSelectedTeams(initialSelectedTeams);
    setSelectedChannels(initialSelectedChannels);
    setSelectedChats(initialSelectedChats);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/microsoft_teams/resources`, {
          headers: { Authorization: `Bearer ${userGoogleId}` },
          credentials: 'include',
        });
        const json = await res.json();
        if (!json.success)
          throw new Error(json.message || 'Failed to load configuration');
        processFetchedData(json.data);
      } catch (err) {
        toast.error(`Error: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [userGoogleId]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    toast.loading('Refreshing Teams list from Microsoft...');
    try {
      const res = await fetch(`${API_URL}/microsoft_teams/resources/refresh`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${userGoogleId}` },
        credentials: 'include',
      });
      const json = await res.json();
      if (!json.success)
        throw new Error(json.message || 'Failed to refresh data');

      processFetchedData(json.data);
      toast.dismiss();
      toast.success('List refreshed successfully!');
    } catch (err) {
      toast.dismiss();
      toast.error(`Error: ${err.message}`);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleSaveSelections = async () => {
    setConfirmLoading(true);
    try {
      const payload = {
        teams: Array.from(selectedTeams),
        chats: Array.from(selectedChats),
        channels: Object.entries(selectedChannels).reduce(
          (acc, [teamId, channelSet]) => {
            if (channelSet.size > 0) acc[teamId] = Array.from(channelSet);
            return acc;
          },
          {},
        ),
      };

      const res = await fetch(`${API_URL}/microsoft_teams/resources/select`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userGoogleId}`,
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      if (!json.success)
        throw new Error(json.message || 'Failed to save selections');

      toast.success('Microsoft Teams selections saved!');
      onClose();
    } catch (err) {
      toast.error(`Error: ${err.message}`);
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleToggleTeam = (teamId) => {
    const newSelected = new Set(selectedTeams);
    if (newSelected.has(teamId)) {
      newSelected.delete(teamId);
      const newChannels = { ...selectedChannels };
      delete newChannels[teamId];
      setSelectedChannels(newChannels);
    } else {
      newSelected.add(teamId);
    }
    setSelectedTeams(newSelected);
  };

  const handleToggleChannel = (teamId, channelId) => {
    const newSelected = { ...selectedChannels };
    if (!newSelected[teamId]) newSelected[teamId] = new Set();
    const teamChannels = new Set(newSelected[teamId]);
    teamChannels.has(channelId)
      ? teamChannels.delete(channelId)
      : teamChannels.add(channelId);
    newSelected[teamId] = teamChannels;
    setSelectedChannels(newSelected);
  };

  const handleToggleChat = (chatId) => {
    const newSelected = new Set(selectedChats);
    newSelected.has(chatId)
      ? newSelected.delete(chatId)
      : newSelected.add(chatId);
    setSelectedChats(newSelected);
  };

  const handleToggleWebhook = async (type, identifiers) => {
    const key =
      identifiers.chat_id || identifiers.channel_id || identifiers.team_id;
    setToggleLoading(key);
    try {
      const res = await fetch(`${API_URL}/microsoft_teams/webhook/toggle`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userGoogleId}`,
        },
        credentials: 'include',
        body: JSON.stringify(identifiers),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.message || 'Toggle failed');

      // Refresh data from DB to get updated webhook statuses
      const refreshRes = await fetch(`${API_URL}/microsoft_teams/resources`, {
        headers: { Authorization: `Bearer ${userGoogleId}` },
        credentials: 'include',
      });
      const refreshJson = await refreshRes.json();
      if (refreshJson.success) processFetchedData(refreshJson.data);

      toast.success(
        `Webhooks ${json.webhook_exists ? 'enabled' : 'disabled'}!`,
      );
    } catch (err) {
      toast.error(`Error: ${err.message}`);
    } finally {
      setToggleLoading(null);
    }
  };

  const channelsByTeam = useMemo(
    () =>
      channels.reduce((acc, channel) => {
        if (!acc[channel.team_id]) acc[channel.team_id] = [];
        acc[channel.team_id].push(channel);
        return acc;
      }, {}),
    [channels],
  );

  const filteredWebhookItems = useMemo(() => {
    const teamMap = new Map(teams.map((t) => [t.team_id, t]));
    const channelMap = new Map(channels.map((c) => [c.channel_id, c]));

    const filteredTeams = Array.from(selectedTeams)
      .map((id) => teamMap.get(id))
      .filter(Boolean);
    const filteredChannels = Object.values(selectedChannels).flatMap(
      (channelSet) =>
        Array.from(channelSet)
          .map((id) => channelMap.get(id))
          .filter(Boolean),
    );
    const filteredChats = chats.filter((c) => selectedChats.has(c.chat_id));

    return {
      teams: filteredTeams,
      channels: filteredChannels,
      chats: filteredChats,
    };
  }, [selectedTeams, selectedChannels, selectedChats, teams, channels, chats]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4">
      <div className="bg-2 flex max-h-[90vh] w-full max-w-3xl flex-col rounded-lg border border-zinc-700 p-6">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-zinc-200">
              Microsoft Teams Configuration
            </h2>
            {lastRefreshed && !loading && (
              <p className="text-xs text-zinc-400">
                List updated: {new Date(lastRefreshed).toLocaleString()}
              </p>
            )}
          </div>
          <div className="flex items-center">
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="mr-2 p-2 text-zinc-400 hover:text-white disabled:cursor-wait disabled:opacity-50"
              title="Refresh List"
            >
              <FiRefreshCw className={isRefreshing ? 'animate-spin' : ''} />
            </button>
            <button
              onClick={onClose}
              className="text-zinc-500 hover:text-white"
            >
              <IoMdClose size={24} />
            </button>
          </div>
        </div>

        <div className="mb-4 flex border-b border-zinc-700">
          <button
            onClick={() => setActiveTab('selection')}
            className={`px-4 py-2 text-sm font-medium ${activeTab === 'selection' ? 'border-b-2 border-[#5652CF] text-white' : 'text-zinc-400'}`}
          >
            Selection
          </button>
          <button
            onClick={() => setActiveTab('webhooks')}
            className={`px-4 py-2 text-sm font-medium ${activeTab === 'webhooks' ? 'border-b-2 border-[#5652CF] text-white' : 'text-zinc-400'}`}
          >
            Webhook Subscriptions
          </button>
        </div>

        <div className="flex-grow overflow-auto pr-2">
          {loading ? (
            <p className="text-center text-zinc-400">
              Loading configuration...
            </p>
          ) : (
            <>
              {activeTab === 'selection' && (
                <div className="space-y-6">
                  <section>
                    <h3 className="text-md mb-2 font-semibold text-zinc-300">
                      Select Teams
                    </h3>
                    <ul className="max-h-48 space-y-2 overflow-y-auto">
                      {teams.map((team) => (
                        <li
                          key={team.team_id}
                          className="rounded-md p-2 hover:bg-zinc-700/50"
                        >
                          <label className="flex cursor-pointer items-center gap-3">
                            <input
                              type="checkbox"
                              checked={selectedTeams.has(team.team_id)}
                              onChange={() => handleToggleTeam(team.team_id)}
                              className="form-checkbox h-5 w-5 border-zinc-600 bg-zinc-900 text-[#5652CF] focus:ring-[#5652CF]"
                            />
                            <div>
                              <span className="text-zinc-300">
                                {team.displayName}
                              </span>
                              {/* --- CHANGE: Show member count --- */}
                              {team.members && (
                                <div className="flex items-center gap-1 text-xs text-zinc-400">
                                  <FiUsers /> {team.members.length} members
                                </div>
                              )}
                            </div>
                          </label>
                        </li>
                      ))}
                    </ul>
                  </section>
                  <section>
                    <h3 className="text-md mb-2 font-semibold text-zinc-300">
                      Select Channels
                    </h3>
                    <div className="max-h-48 space-y-2 overflow-y-auto">
                      {Array.from(selectedTeams).length > 0 ? (
                        teams
                          .filter((t) => selectedTeams.has(t.team_id))
                          .map((team) => (
                            <div
                              key={team.team_id}
                              className="rounded-lg border border-zinc-700 bg-zinc-800"
                            >
                              <button
                                onClick={() =>
                                  setExpandedTeamId((prev) =>
                                    prev === team.team_id ? null : team.team_id,
                                  )
                                }
                                className="flex w-full items-center justify-between p-3"
                              >
                                <span className="font-medium text-zinc-200">
                                  {team.displayName}
                                </span>
                                <IoIosArrowDown
                                  className={`transition-transform ${expandedTeamId === team.team_id ? 'rotate-180' : ''}`}
                                />
                              </button>
                              {expandedTeamId === team.team_id && (
                                <ul className="space-y-2 border-t border-zinc-700 p-3">
                                  {(channelsByTeam[team.team_id] || []).map(
                                    (channel) => (
                                      <li
                                        key={channel.channel_id}
                                        className="rounded-md p-2 hover:bg-zinc-700/50"
                                      >
                                        <label className="flex cursor-pointer items-center gap-3">
                                          <input
                                            type="checkbox"
                                            checked={
                                              selectedChannels[
                                                team.team_id
                                              ]?.has(channel.channel_id) ||
                                              false
                                            }
                                            onChange={() =>
                                              handleToggleChannel(
                                                team.team_id,
                                                channel.channel_id,
                                              )
                                            }
                                            className="form-checkbox h-5 w-5 border-zinc-600 bg-zinc-900 text-[#5652CF] focus:ring-[#5652CF]"
                                          />
                                          <span className="text-zinc-300">
                                            {channel.displayName}
                                          </span>
                                        </label>
                                      </li>
                                    ),
                                  )}
                                </ul>
                              )}
                            </div>
                          ))
                      ) : (
                        <p className="px-2 text-sm text-zinc-500">
                          Select a team above to see its channels.
                        </p>
                      )}
                    </div>
                  </section>
                  <section>
                    <h3 className="text-md mb-2 font-semibold text-zinc-300">
                      Select Chats
                    </h3>
                    <ul className="max-h-48 space-y-2 overflow-y-auto">
                      {chats.map((chat) => (
                        <li
                          key={chat.chat_id}
                          className="rounded-md p-2 hover:bg-zinc-700/50"
                        >
                          <label className="flex cursor-pointer items-center gap-3">
                            <input
                              type="checkbox"
                              checked={selectedChats.has(chat.chat_id)}
                              onChange={() => handleToggleChat(chat.chat_id)}
                              className="form-checkbox h-5 w-5 border-zinc-600 bg-zinc-900 text-[#5652CF] focus:ring-[#5652CF]"
                            />
                            <div>
                              <span className="truncate text-zinc-300">
                                {chat.topic ||
                                  `Chat with ${chat.members.map((m) => m.displayName).join(', ')}`}
                              </span>
                              {/* --- CHANGE: Show member count --- */}
                              {chat.members && (
                                <div className="flex items-center gap-1 text-xs text-zinc-400">
                                  <FiUsers /> {chat.members.length} members
                                </div>
                              )}
                            </div>
                          </label>
                        </li>
                      ))}
                    </ul>
                  </section>
                </div>
              )}
              {activeTab === 'webhooks' && (
                <div className="space-y-6">
                  <WebhookTable
                    title="Teams"
                    items={filteredWebhookItems.teams}
                    type="team"
                    onToggle={handleToggleWebhook}
                    loading={toggleLoading}
                  />
                  <WebhookTable
                    title="Channels"
                    items={filteredWebhookItems.channels}
                    type="channel"
                    onToggle={handleToggleWebhook}
                    loading={toggleLoading}
                  />
                  <WebhookTable
                    title="Chats"
                    items={filteredWebhookItems.chats}
                    type="chat"
                    onToggle={handleToggleWebhook}
                    loading={toggleLoading}
                  />
                </div>
              )}
            </>
          )}
        </div>

        <div className="mt-6 flex items-center justify-end border-t border-zinc-700 pt-4">
          {activeTab === 'selection' && (
            <button
              onClick={handleSaveSelections}
              disabled={confirmLoading}
              className="rounded-md bg-blue-600 px-4 py-2 font-bold text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {confirmLoading ? 'Saving...' : 'Save Selections'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const WebhookTable = ({ title, items, type, onToggle, loading }) => {
  if (items.length === 0)
    return (
      <p className="px-2 text-sm text-zinc-500">{`No ${title.toLowerCase()} selected. Go to the 'Selection' tab to choose which ${title.toLowerCase()} to manage.`}</p>
    );

  return (
    <div className="overflow-x-auto">
      <h3 className="text-md mb-2 font-semibold text-zinc-300">{title}</h3>
      <table className="w-full rounded-lg bg-zinc-800 text-left text-sm">
        <thead className="bg-zinc-800">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase text-zinc-400">
              Name
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase text-zinc-400">
              Webhook Status
            </th>
            <th className="px-4 py-3 text-center text-xs font-medium uppercase text-zinc-400">
              Action
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-700">
          {items.map((it) => {
            const id = it.chat_id || it.channel_id || it.team_id;
            const label =
              it.displayName ||
              it.topic ||
              `Chat with ${it.members?.map((m) => m.displayName).join(', ')}`;
            return (
              <tr key={id} className="hover:bg-zinc-700/50">
                <td className="px-4 py-3">
                  <div className="font-medium text-zinc-200">{label}</div>
                  {/* --- CHANGE: Show member list in webhook table --- */}
                  {it.members && it.members.length > 0 && (
                    <div className="mt-1 truncate text-xs text-zinc-400">
                      With:{' '}
                      {it.members
                        .slice(0, 3)
                        .map((m) => m.displayName)
                        .join(', ')}
                      {it.members.length > 3 &&
                        `, +${it.members.length - 3} more`}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-zinc-300">
                  {it.webhook_exists ? 'Enabled' : 'Disabled'}
                </td>
                <td className="px-4 py-3 text-center">
                  <button
                    onClick={() =>
                      onToggle(type, {
                        team_id: it.team_id,
                        channel_id: it.channel_id,
                        chat_id: it.chat_id,
                      })
                    }
                    disabled={!!loading}
                    className={`transform rounded-md px-4 py-2 text-xs shadow transition duration-200 focus:outline-none ${it.webhook_exists ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'} text-white disabled:opacity-50`}
                  >
                    {loading === id
                      ? '...'
                      : it.webhook_exists
                        ? 'Delete'
                        : 'Create'}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default TeamsConfigurationModal;
